// Template colors are intentionally fixed; PDF/DOCX outputs are theme-invariant.
// Light/Dark variants are picked from the user's personalization.
import {
  AlignmentType,
  BorderStyle,
  Document,
  ImageRun,
  Paragraph,
  TextRun,
} from 'docx';
import { ResumeDocument, ResumePersonalization } from '../../../types';
import {
  escapeHtml,
  formatPeriod,
  formatPhone,
  nl2br,
  parseImageDataUri,
  resolvePersonalization,
  scaledHalfPoints,
  scaledPx,
} from '../helpers';

const PHOTO_PX = 96;

interface Labels {
  summary: string;
  experience: string;
  education: string;
  skills: string;
  languages: string;
  links: string;
  present: string;
}

interface Palette {
  bg: string;
  text: string;
  muted: string;
  subtleText: string;
  rule: string;
}

const LIGHT: Palette = {
  bg: 'FFFFFF',
  text: '111111',
  muted: '6B7280',
  subtleText: '444444',
  rule: '111111',
};
const DARK: Palette = {
  bg: '0F1115',
  text: 'F2F3F5',
  muted: 'A1A6AE',
  subtleText: 'C7CAD1',
  rule: 'F2F3F5',
};

export function toHtml(
  r: ResumeDocument,
  t: Labels,
  personalization: ResumePersonalization
): string {
  const typo = resolvePersonalization(personalization);
  const p = typo.colorMode === 'dark' ? DARK : LIGHT;
  const pi = r.personalInfo;
  const contactLine = [
    pi.email,
    formatPhone(pi.phoneCountryCode, pi.phone),
    pi.location,
    pi.website,
  ]
    .filter(Boolean)
    .map(escapeHtml)
    .join('   ·   ');

  const section = (title: string, inner: string) =>
    inner ? `<section><h2>${escapeHtml(title)}</h2>${inner}</section>` : '';

  const experienceHtml = r.experience
    .map(
      (e) => `
      <div class="entry">
        <div class="row">
          <span class="strong">${escapeHtml(e.position || '')}</span>
          <span class="muted">${escapeHtml(formatPeriod(e.startDate, e.endDate, e.current, t.present))}</span>
        </div>
        <div class="muted small">${escapeHtml(e.company || '')}${e.location ? ` · ${escapeHtml(e.location)}` : ''}</div>
        ${e.description ? `<p>${nl2br(e.description)}</p>` : ''}
      </div>`
    )
    .join('');

  const educationHtml = r.education
    .map(
      (e) => `
      <div class="entry">
        <div class="row">
          <span class="strong">${escapeHtml(e.degree || '')}${e.field ? ` · ${escapeHtml(e.field)}` : ''}</span>
          <span class="muted">${escapeHtml(formatPeriod(e.startDate, e.endDate, false, t.present))}</span>
        </div>
        <div class="muted small">${escapeHtml(e.institution || '')}</div>
        ${e.description ? `<p>${nl2br(e.description)}</p>` : ''}
      </div>`
    )
    .join('');

  const skillsHtml = r.skills.length
    ? `<p>${r.skills.map(escapeHtml).join('   ·   ')}</p>`
    : '';
  const languagesHtml = r.languages.length
    ? `<p>${r.languages
        .map((l) =>
          l.proficiency
            ? `${escapeHtml(l.name)} <span class="muted">(${escapeHtml(l.proficiency)})</span>`
            : escapeHtml(l.name)
        )
        .join('   ·   ')}</p>`
    : '';
  const linksHtml = r.links.length
    ? `<p>${r.links
        .map(
          (l) =>
            `${escapeHtml(l.label || l.url)}${l.url ? ` <span class="muted">${escapeHtml(l.url)}</span>` : ''}`
        )
        .join('<br/>')}</p>`
    : '';

  return `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(r.name)}</title>
<style>
  html, body { background: #${p.bg}; }
  body { font-family: ${typo.fontFamilyHtml}; color: #${p.text}; max-width: 720px; margin: 48px auto; padding: 0 32px; font-size: ${scaledPx(17.5, typo.scale.body)}; line-height: 1.6; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  h1 { font-size: ${scaledPx(42, typo.scale.title)}; font-weight: 300; margin: 0; letter-spacing: 0.5px; }
  .photo { width: ${PHOTO_PX}px; height: ${PHOTO_PX}px; border-radius: 50%; object-fit: cover; margin-bottom: 18px; display: block; }
  .title { font-size: ${scaledPx(19.6, typo.scale.subtitle)}; color: #${p.subtleText}; margin-top: 4px; }
  .contact { font-size: ${scaledPx(16.8, typo.scale.body)}; color: #${p.muted}; margin-top: 10px; }
  h2 { font-size: ${scaledPx(15.4, typo.scale.subtitle)}; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; margin: 28px 0 10px; }
  h2:after { content: ''; display: block; width: 36px; height: 1px; background: #${p.rule}; margin-top: 6px; }
  section { margin-bottom: 6px; }
  .entry { margin-bottom: 14px; }
  .row { display: flex; justify-content: space-between; gap: 12px; }
  .strong { font-weight: 600; }
  .muted { color: #${p.muted}; }
  .small { font-size: ${scaledPx(16.8, typo.scale.body)}; }
  p { margin: 6px 0 0; white-space: pre-wrap; }
  @page { margin: 18mm; size: A4; }
</style></head><body>
  ${pi.photo ? `<img class="photo" src="${pi.photo}" alt=""/>` : ''}
  <h1>${escapeHtml(pi.fullName || '')}</h1>
  ${pi.title ? `<div class="title">${escapeHtml(pi.title)}</div>` : ''}
  ${contactLine ? `<div class="contact">${contactLine}</div>` : ''}
  ${section(t.summary, pi.summary ? `<p>${nl2br(pi.summary)}</p>` : '')}
  ${section(t.experience, experienceHtml)}
  ${section(t.education, educationHtml)}
  ${section(t.skills, skillsHtml)}
  ${section(t.languages, languagesHtml)}
  ${section(t.links, linksHtml)}
</body></html>`;
}

export function toDocx(
  r: ResumeDocument,
  t: Labels,
  personalization: ResumePersonalization
): Document {
  const typo = resolvePersonalization(personalization);
  const p = typo.colorMode === 'dark' ? DARK : LIGHT;
  const font = typo.fontFamilyDocx;

  const titleSize = scaledHalfPoints(30.8, typo.scale.title);
  const subtitleSize = scaledHalfPoints(15.4, typo.scale.subtitle);
  const bodySize = scaledHalfPoints(15.4, typo.scale.body);
  const smallSize = scaledHalfPoints(14, typo.scale.body);

  const heading = (text: string) =>
    new Paragraph({
      spacing: { before: 280, after: 60 },
      border: {
        bottom: { color: p.rule, style: BorderStyle.SINGLE, size: 4, space: 1 },
      },
      children: [
        new TextRun({
          text: text.toUpperCase(),
          bold: true,
          size: subtitleSize,
          characterSpacing: 60,
          color: p.text,
          font,
        }),
      ],
    });

  const para = (text: string, size?: number, color?: string) =>
    new Paragraph({
      spacing: { after: 60 },
      children: [
        new TextRun({
          text,
          size: size ?? bodySize,
          color: color ?? p.text,
          font,
        }),
      ],
    });

  const pi = r.personalInfo;
  const children: Paragraph[] = [];

  const photo = parseImageDataUri(pi.photo);
  if (photo) {
    children.push(
      new Paragraph({
        alignment: AlignmentType.LEFT,
        spacing: { after: 120 },
        children: [
          new ImageRun({
            data: photo.base64,
            type: photo.type,
            transformation: { width: PHOTO_PX, height: PHOTO_PX },
          }),
        ],
      })
    );
  }

  children.push(
    new Paragraph({
      alignment: AlignmentType.LEFT,
      spacing: { after: 40 },
      children: [
        new TextRun({ text: pi.fullName || '', size: titleSize, color: p.text, font }),
      ],
    })
  );
  if (pi.title) {
    children.push(
      new Paragraph({
        spacing: { after: 60 },
        children: [
          new TextRun({
            text: pi.title,
            size: subtitleSize,
            color: p.subtleText,
            font,
          }),
        ],
      })
    );
  }
  const contactLine = [
    pi.email,
    formatPhone(pi.phoneCountryCode, pi.phone),
    pi.location,
    pi.website,
  ]
    .filter(Boolean)
    .join('   ·   ');
  if (contactLine) {
    children.push(
      new Paragraph({
        spacing: { after: 160 },
        children: [
          new TextRun({ text: contactLine, size: smallSize, color: p.muted, font }),
        ],
      })
    );
  }

  if (pi.summary) {
    children.push(heading(t.summary));
    children.push(para(pi.summary));
  }

  if (r.experience.length) {
    children.push(heading(t.experience));
    r.experience.forEach((e) => {
      children.push(
        new Paragraph({
          spacing: { after: 20 },
          children: [
            new TextRun({
              text: e.position || '',
              bold: true,
              size: bodySize,
              color: p.text,
              font,
            }),
            new TextRun({
              text: `   ${formatPeriod(e.startDate, e.endDate, e.current, t.present)}`,
              color: p.muted,
              size: smallSize,
              font,
            }),
          ],
        })
      );
      const sub = [e.company, e.location].filter(Boolean).join(' · ');
      if (sub) children.push(para(sub, smallSize, p.muted));
      if (e.description) children.push(para(e.description));
    });
  }

  if (r.education.length) {
    children.push(heading(t.education));
    r.education.forEach((e) => {
      const head = e.field ? `${e.degree || ''} · ${e.field}` : e.degree || '';
      children.push(
        new Paragraph({
          spacing: { after: 20 },
          children: [
            new TextRun({
              text: head,
              bold: true,
              size: bodySize,
              color: p.text,
              font,
            }),
            new TextRun({
              text: `   ${formatPeriod(e.startDate, e.endDate, false, t.present)}`,
              color: p.muted,
              size: smallSize,
              font,
            }),
          ],
        })
      );
      if (e.institution) children.push(para(e.institution, smallSize, p.muted));
      if (e.description) children.push(para(e.description));
    });
  }

  if (r.skills.length) {
    children.push(heading(t.skills));
    children.push(para(r.skills.join('   ·   ')));
  }

  if (r.languages.length) {
    children.push(heading(t.languages));
    children.push(
      para(
        r.languages
          .map((l) => (l.proficiency ? `${l.name} (${l.proficiency})` : l.name))
          .join('   ·   ')
      )
    );
  }

  if (r.links.length) {
    children.push(heading(t.links));
    r.links.forEach((l) => {
      const text = l.url ? `${l.label || l.url} — ${l.url}` : l.label || '';
      if (text) children.push(para(text, smallSize));
    });
  }

  return new Document({
    background: { color: p.bg },
    sections: [
      {
        properties: { page: { margin: { top: 1080, bottom: 1080, left: 1080, right: 1080 } } },
        children,
      },
    ],
  });
}

export default {};
